/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { useMemo } from "react";
import { useScrapLoadingLayout } from "components/scrapEyeLabelling/hooks/useScrapLoadingLayout";
import { useAreaHotkeys } from "components/scrapEyeLabelling/hooks/useAreaHotkeys";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { useTenantTranslation } from "hooks/formatters";
import { BayStyleLayout } from "./BayLayout";
import { GridStyleLayout } from "./GridLayout";
import { RailCarStyleLayout } from "./RailCarLayout";
import { AreaColorMap, AreaMaterialsMap } from "./scrapEyeLayoutCommon";

const NON_SELECTABLE_BY_DEFAULT = ["ECS", "Cab"];

type LayoutData = NonNullable<
  ReturnType<typeof useScrapLoadingLayout>["layout"]
>;

const drawnAreaIds = (layout: LayoutData): string[] => {
  if ("areas" in layout) {
    return layout.areas.map((area) => area.area_id);
  }
  if ("tracks" in layout) {
    return layout.tracks.flatMap((track) =>
      track.areas.map((area) => area.area_id)
    );
  }
  return layout.columns.flat().filter((name): name is string => name !== null);
};

export type ScrapEyeLayoutMapProps = Readonly<{
  onSelectArea?: (area: string) => void;
  nonSelectableAreas?: string[];
  areaColors?: AreaColorMap;
  areaMaterials?: AreaMaterialsMap;
  selectedArea?: string | null;
  location?: string;
  /** Track-car areas with no material yet pulse while the expected materials load. */
  isLoadingAreaMaterials?: boolean;
}>;

export const ScrapEyeLayoutMap = (props: ScrapEyeLayoutMapProps) => {
  const { t } = useTenantTranslation();

  const {
    onSelectArea,
    areaColors,
    areaMaterials,
    selectedArea,
    location = "crane",
    isLoadingAreaMaterials,
  } = props;

  const areaHotkeys = useAreaHotkeys(location);
  const { available_areas: availableAreas } = useScrapEyeMetadata();

  const { layout, layoutType, isLoading } = useScrapLoadingLayout(location);

  const nonSelectableAreas = useMemo(() => {
    if (props.nonSelectableAreas !== undefined) {
      return props.nonSelectableAreas;
    }
    const configured = new Set(availableAreas ?? []);
    const drawn = layout === undefined ? [] : drawnAreaIds(layout);
    return [
      ...NON_SELECTABLE_BY_DEFAULT,
      ...drawn.filter((areaId) => !configured.has(areaId)),
    ];
  }, [props.nonSelectableAreas, availableAreas, layout]);

  if (isLoading || !layout || !layoutType) {
    return null;
  }

  if (layoutType === "railcar") {
    return (
      <RailCarStyleLayout
        layout={layout}
        onSelectArea={onSelectArea}
        nonSelectableAreas={nonSelectableAreas}
        areaHotkeys={areaHotkeys}
        areaColors={areaColors}
        areaMaterials={areaMaterials}
        selectedArea={selectedArea}
        isLoadingAreaMaterials={isLoadingAreaMaterials}
      />
    );
  }

  if (layoutType === "grid") {
    return (
      <GridStyleLayout
        layout={layout}
        onSelectArea={onSelectArea}
        nonSelectableAreas={nonSelectableAreas}
        areaHotkeys={areaHotkeys}
        areaColors={areaColors}
        areaMaterials={areaMaterials}
        selectedArea={selectedArea}
      />
    );
  }

  return (
    <BayStyleLayout
      columns={layout.columns}
      onSelectArea={onSelectArea}
      nonSelectableAreas={nonSelectableAreas}
      areaHotkeys={areaHotkeys}
      areaColors={areaColors}
      areaMaterials={areaMaterials}
      cabLabel={t("cab")}
      selectedArea={selectedArea}
    />
  );
};
